<!-- 都道府県・市区町村・業種選択スライド -->
<script setup lang="ts">
import { computed, watch } from 'vue';

import type Inquiry from '@/javascripts/utils/inquiry';
import { getPrefectures, getPositionTypes } from '@/javascripts/utils/inquiry_form_options';

import { useCity } from '@/javascripts/composables/inquiry/city';
import type { InquiryValidation } from '@/javascripts/composables/inquiry/validation';

/** プロパティ */
const props = defineProps<{
  /** 問い合わせオブジェクト */
  prefectureCd: Inquiry['prefectureCd'],
  cityCd: Inquiry['cityCd'],
  positionTypeCds: Inquiry['positionTypeCds'],
  /** 問い合わせバリデーション */
  v$: InquiryValidation
}>();

/** カスタムイベント */
const emit = defineEmits([
  'back', 
  'next',
  'update:prefecture-cd', 
  'update:city-cd', 
  'update:position-type-cds'
]);

/** フォーム項目の computed プロパティ */
const localPrefectureCd = computed({
  get: () => props.prefectureCd,
  set: (value) => emit('update:prefecture-cd', value)
});
const localCityCd = computed({
  get: () => props.cityCd,
  set: (value) => emit('update:city-cd', value)
});
const localPositionTypeCds = computed({
  get: () => props.positionTypeCds,
  set: (value) => emit('update:position-type-cds', value)
});

/** 都道府県の選択肢リスト */
const prefectures = getPrefectures();

const {
  /** 市区町村の選択肢リスト */
  cities,
  /** 市区町村取得エラー */
  error
} = useCity(localPrefectureCd);

// 都道府県が変更されたら、市区町村の選択をリセットする
watch(localPrefectureCd, () => {
  localCityCd.value = null;
});

/** 業種の選択肢リスト */
const positionTypes = getPositionTypes();
</script>

<template>
  <div class="slide_body">
    <div class="option_wrap">
      <h3 class="option_title required">
        お住まいのエリアを教えてください
      </h3>
      <div class="option_group mb16">
        <div class="option_input">
          <select
            v-model="localPrefectureCd"
            required
          >
            <option :value="null" disabled>都道府県</option>
            <option
              v-for="prefecture in prefectures"
              :key="prefecture.cd"
              :value="prefecture.cd"
            >{{ prefecture.text }}</option>
          </select>
        </div>
        <div class="option_input">
          <select
            v-model="localCityCd"
          >
            <option
              :value="null"
              selected
              hidden
            >市区町村(任意)</option>
            <option v-if="error" disabled>{{ error }}</option>
            <option
              v-for="city in cities"
              :key="city.cd"
              :value="city.cd"
            >{{ city.text }}</option>
          </select>
        </div>
      </div>
      <div
        v-for="(error, key) in v$.prefectureCd.$errors"
        :key="key"
        class="validation_message_box"
      >{{ error.$message }}</div>
      <h3 class="option_title">どの業種でお探しですか？<br/><span class="annotation">(複数選択OK)</span></h3>
      <div class="option_group">
        <div
          v-for="positionType in positionTypes"
          :key="positionType.cd"
          class="option_input"
        >
          <input
            :id="`position_type_cd_${positionType.cd}`"
            v-model="localPositionTypeCds"
            type="checkbox"
            name="position_type_cd"
            :value="positionType.cd"
          >
          <label
            :for="`position_type_cd_${positionType.cd}`"
          >{{ positionType.text }}</label>
        </div>
      </div>
    </div>
    <div class="slide_control_group">
      <a
        id="ph_back_2"
        class="back_link"
        @click="emit('back')"
      >&lt; 戻る</a>
      <button
        id="ph_next_3"
        :disabled="v$.prefectureCd.$invalid"
        class="form_button next_button"
        @click="emit('next')"
      >
        次へ
      </button>
    </div>
  </div>
</template>